import React from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { formatNarrowChapterChip } from "../../bible/bookNames";
import { readerChapterChipSx } from "../../theme/readerLayout";

export const computeChapterMeta = ({
  verseCount = 0,
  verseTextMap = {},
  isEn = false,
}) => {
  const texts = Object.values(verseTextMap || {});
  let count = 0;
  if (isEn) {
    texts.forEach((t) => {
      const words = String(t || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      count += words.length;
    });
  } else {
    texts.forEach((t) => {
      count += String(t || "").replace(/\s+/g, "").length;
    });
  }
  const rate = isEn ? 200 : 400;
  const minutes = Math.max(1, Math.ceil(count / rate));
  return {
    verseCount,
    wordCount: count,
    minutes,
  };
};

const ReaderHeader = ({
  chapter,
  bookName,
  bookTotalChapters,
  chapterSubtitle,
  currentChapterText,
  verseCount = 0,
  verseTextMap = {},
  edition,
  isMobile,
  onBack,
  wakeLock,
  isFullscreen,
  isFullscreenSupported,
  toggleFullscreen,
  isZenMode,
  toggleZenMode,
}) => {
  if (!chapter) return null;

  const isEn = edition?.id === "en";
  const meta = computeChapterMeta({
    verseCount,
    verseTextMap,
    isEn,
  });
  const metaText =
    verseCount > 0
      ? edition?.ui?.chapterMetaLine
        ? edition.ui.chapterMetaLine(
            meta.verseCount,
            meta.wordCount,
            meta.minutes
          )
        : `共 ${meta.verseCount} 节 · 约 ${meta.wordCount} 字 · 预计 ${meta.minutes} 分钟`
      : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mb: { xs: 2.5, sm: 4 },
        gap: { xs: 1, sm: 1.5 },
      }}
    >
      <IconButton
        onClick={onBack}
        sx={{
          p: { xs: 0.75, sm: 1 },
          bgcolor: "action.hover",
          "&:hover": { bgcolor: "action.selected" },
          flexShrink: 0,
        }}
        aria-label={edition.ui.back}
      >
        <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {currentChapterText && (
          <Box sx={{ mb: 0.75 }}>
            <Chip
              label={
                isMobile
                  ? formatNarrowChapterChip({
                      bookName,
                      chapterNumber: chapter.chapterNumber,
                      total: bookTotalChapters,
                    })
                  : currentChapterText
              }
              size="small"
              color="primary"
              variant="outlined"
              sx={readerChapterChipSx}
            />
          </Box>
        )}
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: "text.primary",
            lineHeight: 1.25,
            fontSize: { xs: "1.35rem", sm: "2.125rem" },
          }}
        >
          {chapter.title.replace("# ", "")}
        </Typography>
        {chapterSubtitle && (
          <Typography
            variant="subtitle1"
            color="primary.main"
            sx={{
              mt: 0.4,
              fontWeight: 600,
              fontStyle: "italic",
              opacity: 0.9,
              fontSize: { xs: "0.95rem", sm: "1.25rem" },
            }}
          >
            {chapterSubtitle}
          </Typography>
        )}
        {metaText && (
          <Typography
            variant="caption"
            data-testid="chapter-meta-line"
            sx={{
              display: "block",
              mt: 0.5,
              color: "text.secondary",
              fontWeight: 500,
              fontSize: { xs: "0.75rem", sm: "0.82rem" },
            }}
          >
            {metaText}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.6,
            color: "text.secondary",
            letterSpacing: "0.02em",
            fontSize: { xs: "0.75rem", sm: "0.85rem" },
          }}
        >
          {isMobile
            ? edition.ui.selectHintMobile
            : edition.ui.selectHintDesktop}
        </Typography>
      </Box>
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          gap: { xs: 0.5, sm: 0.75 },
          flexShrink: 0,
        }}
      >
        {wakeLock?.isSupported && (
          <Tooltip
            title={
              wakeLock.isEnabled
                ? wakeLock.isActive
                  ? edition.ui.wakeLockActive || "屏幕常亮中 (点击关闭)"
                  : edition.ui.wakeLockActive || "保持常亮 (点击关闭)"
                : edition.ui.wakeLockInactive || "屏幕常亮已关闭 (点击开启)"
            }
          >
            <IconButton
              onClick={wakeLock.toggleEnabled}
              aria-label={
                wakeLock.isEnabled
                  ? edition.ui.wakeLockActive || "关闭屏幕常亮"
                  : edition.ui.wakeLockInactive || "开启屏幕常亮"
              }
              sx={{
                color: wakeLock.isActive ? "warning.main" : "text.secondary",
                bgcolor: (theme) =>
                  wakeLock.isActive
                    ? theme.palette.mode === "dark"
                      ? "rgba(255, 179, 0, 0.16)"
                      : "rgba(255, 160, 0, 0.12)"
                    : theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(0, 0, 0, 0.04)",
                "&:hover": { bgcolor: "action.hover" },
                p: { xs: 0.75, sm: 1 },
              }}
            >
              {wakeLock.isActive ? (
                <LightbulbIcon fontSize={isMobile ? "small" : "medium"} />
              ) : (
                <LightbulbOutlinedIcon fontSize={isMobile ? "small" : "medium"} />
              )}
            </IconButton>
          </Tooltip>
        )}
        {isFullscreenSupported && (
          <Tooltip
            title={
              isFullscreen
                ? edition.ui.exitFullscreen || "退出全屏 (Esc)"
                : edition.ui.fullscreen || "全屏阅读 (F)"
            }
          >
            <IconButton
              onClick={toggleFullscreen}
              aria-label={
                isFullscreen
                  ? edition.ui.exitFullscreen || "退出全屏"
                  : edition.ui.fullscreen || "全屏阅读"
              }
              sx={{
                color: isFullscreen ? "primary.main" : "text.secondary",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.04)",
                "&:hover": { bgcolor: "action.hover" },
                p: { xs: 0.75, sm: 1 },
              }}
            >
              {isFullscreen ? (
                <FullscreenExitIcon fontSize={isMobile ? "small" : "medium"} />
              ) : (
                <FullscreenIcon fontSize={isMobile ? "small" : "medium"} />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          title={
            isZenMode
              ? edition.ui.exitZenMode || "退出专注 (Esc)"
              : edition.ui.zenMode || "专注阅读 (Z)"
          }
        >
          <IconButton
            onClick={toggleZenMode}
            aria-label={edition.ui.zenMode || "专注模式"}
            sx={{
              color: isZenMode ? "primary.main" : "text.secondary",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(0, 0, 0, 0.04)",
              "&:hover": { bgcolor: "action.hover" },
              p: { xs: 0.75, sm: 1 },
            }}
          >
            {isZenMode ? (
              <CloseFullscreenIcon fontSize={isMobile ? "small" : "medium"} />
            ) : (
              <SelfImprovementIcon fontSize={isMobile ? "small" : "medium"} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ReaderHeader;
